import Icon, {
  CheckCircleFilled,
  CloseCircleFilled,
  DownOutlined,
  PlusOutlined,
  RightOutlined,
} from '@ant-design/icons'
import { Button, Form, message, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import useToken from 'antd/es/theme/useToken'
import { createContext, useContext, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ReactComponent as DoctorIconFilled } from '../../assets/Icons_Doctor fill.svg'
import ActionDropdown, {
  ActionDropdownItemsType,
} from '../../components/ActionDropdown/ActionDropdown'
import { displayMessage } from '../../helpers/Message'
import {
  activateDoctor,
  addDoctor,
  changeDoctorRole,
  deleteDoctor,
  errorDoctor,
  getDoctors,
  selectAllDoctors,
  selectDoctorLoadingState,
  selectDoctorByKey,
  selectErrorInDoctor,
  selectSuccessInDoctor,
  successDoctor,
} from '../../state/doctor/doctorReducer'
import { Doctor } from '../../state/doctor/types'
import { DoctorViewType } from '../../types/DoctorViewTypeIndex'
import Header from '../CyHeader/CyHeader'
import NewDoctorView from '../NewDoctorView/NewDoctorView'
import { generateFullName } from '../PatientsView/PatientsViewStyle'
import { DoctorTabs } from './DoctorTabs'
import { DoctorViewContainer } from './DoctorsViewStyle'

type DoctorContextType = {
  updateViewState: boolean
  setOpenViewStates: React.Dispatch<
    React.SetStateAction<
      Record<
        string,
        {
          updateState: boolean
        }
      >
    >
  >
}

const DoctorContext = createContext<DoctorContextType | undefined>(undefined)

export const useDoctorContext = (): DoctorContextType => {
  const context = useContext(DoctorContext)
  return context!
}

function DoctorsView() {
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const [theme, token] = useToken()
  const [messageApi, contextHolder] = message.useMessage()
  const doctors = useSelector(selectAllDoctors)
  const doctorError = useSelector(selectErrorInDoctor)
  const doctorSuccess = useSelector(selectSuccessInDoctor)
  const [openViewStates, setOpenViewStates] = useState(intialOpenViewStates())
  const [expandedKey, setExpandedKey] = useState<string>('')
  const [openNewDoctorDrawer, setOpenNewDoctorDrawer] = useState(false)
  const doctorLoadingStatus = useSelector(selectDoctorLoadingState)
  const [tableScroll, setTableScroll] = useState<{ x?: number; y?: number }>({ y: 0 });

  const columns: ColumnsType<DoctorViewType> = [
    {
      title: 'Doctor ID',
      dataIndex: 'id',
      key: 'id',
      sorter: { compare: (a, b) => a.id.localeCompare(b.id) },
      width: '120px', 
    },
    {
      title: 'Doctor Name',
      dataIndex: 'name',
      key: 'name',
      sorter: { compare: (a, b) => a.name.localeCompare(b.name) },
      width: '120px', 
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      sorter: { compare: (a, b) => a.phone.localeCompare(b.phone) },
      width: '120px', 
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: { compare: (a, b) => a.email.localeCompare(b.email) },
      width: '120px', 
    },
    {
      title: 'Supervisor',
      dataIndex: 'supervisor',
      key: 'supervisor',
      sorter: { compare: (a, b) => a.supervisor.localeCompare(b.supervisor) },
      width: '120px', 
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (info: string) => <StatusIcon info={info} />,
      className: 'ant-center',
      sorter: {
        compare: (a, b) => (a.status || '').localeCompare(b.status || ''),
      },
      defaultSortOrder: 'descend',
      width: '120px', 
    },
    {
      title: 'Action',
      dataIndex: 'doctorKey',
      className: 'ant-center',
      key: 'action',
      render: (doctorKey) => <DoctorActionDropdown doctorKey={doctorKey} />,
      width: '120px', 
    },
  ]

  useEffect(() => {
    dispatch(getDoctors())
  }, [dispatch])

  useEffect(() => {
    const updateScroll = () => {        
      setTableScroll(
        window.innerWidth <= 1200
          ? { x: 120 * columns.length, y: 0 } 
          : { y: 0 } 
      );
    };
    
    updateScroll();
    window.addEventListener('resize', updateScroll);
    return () => window.removeEventListener('resize', updateScroll);
  }, [columns.length]);

  function intialOpenViewStates() {
    let data: Record<
      string,
      {
        updateState: boolean
      }
    > = {}
    doctors?.forEach((doctor) => {
      data[doctor.doctorKey] = {
        updateState: false,
      }
    })
    return data
  }
  useEffect(() => {
    let keys: string[] = []
    Object.entries(openViewStates).forEach(([key, value]) => {
      if (value.updateState) {
        keys.push(key)
        setExpandedKey((prev) => key)
      }
    })
  }, [openViewStates])

  const updateEvent = async (doctorKey: string) => {
    let oldExpandedElement = document.querySelector(
      `tr[data-row-key="${expandedKey}"]`
    ) as HTMLElement | null
    if (oldExpandedElement) oldExpandedElement.style.background = ''
    setExpandedKey(expandedKey === doctorKey ? '' : doctorKey)
    if (expandedKey === doctorKey) {
      let ExpandedElement = document.querySelector(
        `tr[data-row-key="${doctorKey}"]`
      ) as HTMLElement | null
      if (ExpandedElement) ExpandedElement.style.background = '#e3e3e3'
    }
    setOpenViewStates((prev) => {
      const newData = { ...prev }
      newData[doctorKey] = {
        updateState: true,
      }
      return newData
    })
  }

  const DoctorActionDropdown = ({ doctorKey }: { doctorKey: string }) => {
    const currentDoctor = useSelector(selectDoctorByKey(doctorKey))
    const currentStatus = currentDoctor?.isActive === 'N'
    const isSupervisor = currentDoctor?.isSupervisor === 'Y'
    const activateStatus = !currentStatus
    const items: ActionDropdownItemsType[] = [
      {
        type: 'toggle',
        onClick: () => {
          dispatch(activateDoctor(currentDoctor!.doctorKey))
        },
        label: 'Reactivate',
        disabled: activateStatus,
        switchState: activateStatus,
      },
      {
        type: 'confirm',
        label: 'Change Role',
        disabled: !activateStatus,
        onClick: () => {
          dispatch(
            changeDoctorRole({
              id: currentDoctor!.doctorKey,
              role: isSupervisor ? 'N' : 'Y',
            })
          )
        },
        popConfirmTitle: isSupervisor
          ? 'Are you Sure to change the role to Doctor?'
          : 'Are you Sure to change the role to Chief Doctor?',
      },
      {
        type: 'normal',
        label: 'Update',
        disabled: !activateStatus,
        onClick: () => {
          updateEvent(doctorKey)
        },
      },
      {
        type: 'confirm',
        onClick: () => {
          dispatch(deleteDoctor(currentDoctor!.doctorKey))
        },
        popConfirmTitle: 'Are you Sure to Delete?',
        label: 'Delete',
        disabled: currentStatus,
      },
    ]

    return <ActionDropdown actionDropdownItems={items} label='...' />
  }

  const StatusIcon = ({ info }: { info: string }) => {
    const statusIcons: Record<string, JSX.Element> = {
      Y: <CheckCircleFilled style={{ color: 'green', fontSize: '30px' }} />,
      N: <CloseCircleFilled style={{ color: 'red', fontSize: '30px' }} />,
    }
    return <>{statusIcons[info]}</>
  }



  const data: DoctorViewType[] = doctors.map((item: Doctor, index) => {
    const isSupervisor = (isSupervisor: string) =>
      isSupervisor === 'Y' ? 'Yes' : 'No'
    return {
      key: item.doctorKey,
      id: item.did,
      name: generateFullName(item.title, item.firstName, item.lastName),
      phone: item.phonePrimary,
      email: item.emailId,
      supervisor: isSupervisor(item.isSupervisor),
      doctorKey: item.doctorKey,
      status: item.isActive,
    }
  })

  const onNewDoctorViewClose = () => {
    setOpenNewDoctorDrawer(false)
  }

  const onFinish = async () => {
    const values = form.getFieldsValue()
    const formData = {
      ...values,
    }
    let keys = Object.keys(formData)
    keys.forEach((key) => {
      if (formData[key] === null) {
        formData[key] = ''
      }
    })
    dispatch(addDoctor(formData))
    form.resetFields()
    onNewDoctorViewClose()
  }

  useEffect(() => {
    if (doctorError) {
      displayMessage(doctorError, 'error', messageApi)
      dispatch(errorDoctor(null))
    }
    if (doctorSuccess) {
      displayMessage(doctorSuccess, 'success', messageApi)
      dispatch(successDoctor(null))
    }
  }, [doctorError, doctorSuccess])

  return (
    <>
      <Header
        title={
          <>
            <Icon component={DoctorIconFilled} style={{ fontSize: '150%' }} />
            <h2>Doctors</h2>
          </>
        }
      />
      <DoctorViewContainer>
        <NewDoctorView
          onFinish={onFinish}
          open={openNewDoctorDrawer}
          onClose={onNewDoctorViewClose}
          form={form}
        />
        {contextHolder}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: '10px',
          }}
        >
          <Button
            type='dashed'
            icon={<PlusOutlined />}
            onClick={() => {
              form.resetFields()
              if (openNewDoctorDrawer) setOpenNewDoctorDrawer(false)
              else setOpenNewDoctorDrawer(true)
            }}
          >
            Register Doctor
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={data}
          loading={doctorLoadingStatus}
          scroll={tableScroll}
          className='pageDataTable'
          pagination={{
            position: ['topLeft'],
            pageSizeOptions: [20, 50, 100, 200],
            defaultPageSize: 20,
          }}
          expandable={{
            expandRowByClick: true,
            expandedRowKeys: [expandedKey],
            onExpand(expanded, record) {
              let oldExpandedElement = document.querySelector(
                `tr[data-row-key="${expandedKey}"]`
              ) as HTMLElement | null
              if (oldExpandedElement) oldExpandedElement.style.background = ''
              setExpandedKey(
                expandedKey === record.doctorKey ? '' : record.doctorKey
              )
              if (expanded) {
                let ExpandedElement = document.querySelector(
                  `tr[data-row-key="${record.doctorKey}"]`
                ) as HTMLElement | null
                if (ExpandedElement)
                  ExpandedElement.style.background = '#e3e3e3'
              }
            },
            expandedRowRender: (record) => {
              const values = {
                updateViewState: openViewStates[record.doctorKey]?.updateState,
              }
              return (
                <DoctorContext.Provider
                  value={{
                    ...values,
                    setOpenViewStates: setOpenViewStates,
                  }}
                >
                  <DoctorTabs
                    status={record.status}
                    doctorKey={record.doctorKey}
                  />
                </DoctorContext.Provider>
              )
            },
            expandIcon: ({ expanded, onExpand, record }) =>
              expanded ? (
                <DownOutlined onClick={(e) => onExpand(record, e)} />
              ) : (
                <RightOutlined onClick={(e) => onExpand(record, e)} />
              ),
          }}
        />
      </DoctorViewContainer>
    </>
  )
}

export default DoctorsView
